import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, MessageCircle, Clock, X, Truck, MapPin, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

const buildNumeroWhatsApp = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55")) return digits;
  return `55${digits}`;
};

const formatHora = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const formatDataBR = (iso) => {
  if (!iso) return "";
  const s = iso.slice(0, 10);
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

export default function PreVendasBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/pre-vendas/notas`);
      setData(data);
    } catch (e) {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  if (!data || !data.dentro_da_janela) return null;
  const totalItems = (data.total_notas || 0) + (data.total_reativacao || 0);
  if (totalItems === 0) return null;

  const todosEnviados = data.total_enviados >= totalItems;

  let btnClasses = "";
  let icon = null;
  let label = "";
  if (todosEnviados) {
    btnClasses = "bg-gray-200 hover:bg-gray-300 text-gray-600 border-gray-300";
    icon = <CheckCircle2 className="h-5 w-5" />;
    label = "✅ Pré Vendas concluído";
  } else {
    btnClasses = "bg-blue-500 hover:bg-blue-600 text-white border-blue-600 shadow-md";
    icon = <TrendingUp className="h-5 w-5" />;
    label = "Pré Vendas da Rota";
  }

  const handleClickWhatsApp = async (nomeCompleto, primeiroNome, telefone, tipoRef, refId) => {
    const numero = buildNumeroWhatsApp(telefone);
    if (!numero) {
      toast.error("Cliente sem telefone");
      return;
    }
    const primeiro = primeiroNome || (nomeCompleto || "").split(" ")[0] || "";
    const msg = `Oi ${primeiro} Tudo bem!! `;
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(msg)}`;
    try {
      await axios.post(`${API}/pre-vendas/click-whatsapp/${tipoRef}/${refId}`);
    } catch (e) {
      // não bloqueia abertura
    }
    window.open(url, "_blank");
    load();
  };

  const handleToggle = async (tipoRef, refId) => {
    try {
      await axios.post(`${API}/pre-vendas/toggle-enviado/${tipoRef}/${refId}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alternar aviso");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="pre-vendas-banner-btn"
        disabled={loading}
        className={`w-full max-w-4xl mx-auto mb-4 rounded-2xl border-2 px-5 py-4 flex items-center justify-between gap-3 font-bold text-sm sm:text-base transition-all ${btnClasses}`}
      >
        <div className="flex items-center gap-3 flex-1 text-left">
          {icon}
          <span className="uppercase tracking-wide">{label}</span>
        </div>
        <span
          className={`text-xs sm:text-sm rounded-full px-3 py-1 font-bold ${
            todosEnviados ? "bg-white/60 text-gray-700" : "bg-white/70 text-blue-900"
          }`}
        >
          {data.total_enviados}/{totalItems}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="pre-vendas-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <TrendingUp className={`h-6 w-6 ${todosEnviados ? "text-gray-400" : "text-blue-600"}`} />
              Pré Vendas da Rota
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Notas vencidas entre {formatDataBR(data.periodo_inicio)} e {formatDataBR(data.periodo_fim)} · {data.total_notas} cobrança(s) · {data.total_reativacao} reativação(ões)
            </p>
          </DialogHeader>

          <div className="space-y-6 pt-2">
            {data.por_carro.length === 0 && (
              <p className="text-center text-gray-400 py-10">
                Nenhuma nota vencida nos últimos 3 dias.
              </p>
            )}

            {data.por_carro.map((carro) => (
              <div key={carro.carro_id} data-testid={`prevendas-carro-${carro.carro_id}`}>
                <div className="flex items-center gap-2 mb-2 pb-1 border-b-2 border-blue-200">
                  <Truck className="h-5 w-5 text-blue-600" />
                  <h3 className="font-bold text-lg text-blue-900 uppercase">
                    {carro.carro_nome}
                  </h3>
                </div>

                {carro.rotas.map((rota) => (
                  <div key={rota.rota_id} className="mb-4">
                    <div className="flex items-center gap-1 mb-2 mt-2">
                      <MapPin className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-semibold text-purple-800">
                        {rota.rota_nome}
                      </span>
                    </div>

                    {/* Bloco 1: Notas vencidas (cobrança) */}
                    {rota.clientes_com_nota_vencida.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center gap-1 mb-1">
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                          <span className="text-[11px] font-bold uppercase tracking-wide text-red-700">
                            Cobrança de notas vencidas
                          </span>
                        </div>
                        <div className="space-y-2 pl-1">
                          {rota.clientes_com_nota_vencida.map((cli) => (
                            <div
                              key={cli.cliente_id}
                              data-testid={`prevendas-cli-nota-${cli.cliente_id}`}
                              className="rounded-xl border bg-white shadow-sm p-3 space-y-2"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-sm truncate">{cli.cliente_nome}</p>
                                <p className="text-xs text-gray-500">
                                  {cli.cidade_estado} · {cli.telefone}
                                </p>
                              </div>

                              {cli.notas.map((nota) => {
                                const enviado = nota.aviso?.enviado;
                                const clickEm = nota.aviso?.click_whatsapp_em;
                                return (
                                  <div
                                    key={nota.promissoria_id}
                                    data-testid={`prevendas-nota-${nota.promissoria_id}`}
                                    className={`rounded-lg border p-2 flex flex-col sm:flex-row sm:items-center gap-2 ${
                                      enviado ? "bg-emerald-50/70 border-emerald-200" : "bg-red-50/50 border-red-200"
                                    }`}
                                  >
                                    <div className="flex-1 text-xs">
                                      <span className="font-semibold">Nota #{nota.numero_pedido}</span>
                                      <span className="text-red-600 ml-1 font-semibold">
                                        · Venc: {formatDataBR(nota.data_vencimento)}
                                      </span>
                                      <span className="ml-1 font-bold text-red-700">
                                        · R$ {(nota.valor_restante || 0).toFixed(2)}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap justify-end">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          handleClickWhatsApp(
                                            cli.cliente_nome,
                                            cli.primeiro_nome,
                                            cli.telefone,
                                            "nota",
                                            nota.promissoria_id
                                          )
                                        }
                                        className="gap-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50 h-8"
                                        data-testid={`btn-wa-nota-${nota.promissoria_id}`}
                                      >
                                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                                      </Button>
                                      <div
                                        className={`flex items-center gap-1 text-xs rounded-md px-2 py-1 min-w-[70px] justify-center ${
                                          clickEm ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-500"
                                        }`}
                                        data-testid={`prevendas-contador-nota-${nota.promissoria_id}`}
                                      >
                                        <Clock className="h-3 w-3" />
                                        {clickEm ? formatHora(clickEm) : "--:--"}
                                      </div>
                                      <button
                                        onClick={() => handleToggle("nota", nota.promissoria_id)}
                                        data-testid={`prevendas-toggle-nota-${nota.promissoria_id}`}
                                        className={`relative inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold transition-colors border-2 ${
                                          enviado
                                            ? "bg-emerald-500 text-white border-emerald-600"
                                            : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                                        }`}
                                      >
                                        <span className={`h-2.5 w-2.5 rounded-full ${enviado ? "bg-white" : "bg-gray-400"}`} />
                                        {enviado ? "ENVIADO" : "ENVIADO?"}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bloco 2: Clientes sem nota (reativação) */}
                    {rota.clientes_sem_nota.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 mb-1 mt-3">
                          <TrendingUp className="h-3.5 w-3.5 text-blue-500" />
                          <span className="text-[11px] font-bold uppercase tracking-wide text-blue-700">
                            Reativar clientes (sem nota ativa)
                          </span>
                        </div>
                        <div className="space-y-2 pl-1">
                          {rota.clientes_sem_nota.map((cli) => {
                            const enviado = cli.aviso?.enviado;
                            const clickEm = cli.aviso?.click_whatsapp_em;
                            return (
                              <div
                                key={cli.cliente_id}
                                data-testid={`prevendas-cli-reativar-${cli.cliente_id}`}
                                className={`rounded-xl border shadow-sm p-3 flex flex-col sm:flex-row sm:items-center gap-2 ${
                                  enviado ? "bg-emerald-50/70 border-emerald-200" : "bg-blue-50/40 border-blue-200"
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-sm truncate">{cli.cliente_nome}</p>
                                  <p className="text-xs text-gray-500">
                                    {cli.cidade_estado} · {cli.telefone}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleClickWhatsApp(
                                        cli.cliente_nome,
                                        cli.primeiro_nome,
                                        cli.telefone,
                                        "cliente",
                                        cli.cliente_id
                                      )
                                    }
                                    className="gap-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50 h-8"
                                    data-testid={`btn-wa-cliente-${cli.cliente_id}`}
                                  >
                                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                                  </Button>
                                  <div
                                    className={`flex items-center gap-1 text-xs rounded-md px-2 py-1 min-w-[70px] justify-center ${
                                      clickEm ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-500"
                                    }`}
                                    data-testid={`prevendas-contador-cli-${cli.cliente_id}`}
                                  >
                                    <Clock className="h-3 w-3" />
                                    {clickEm ? formatHora(clickEm) : "--:--"}
                                  </div>
                                  <button
                                    onClick={() => handleToggle("cliente", cli.cliente_id)}
                                    data-testid={`prevendas-toggle-cli-${cli.cliente_id}`}
                                    className={`relative inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold transition-colors border-2 ${
                                      enviado
                                        ? "bg-emerald-500 text-white border-emerald-600"
                                        : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                                    }`}
                                  >
                                    <span className={`h-2.5 w-2.5 rounded-full ${enviado ? "bg-white" : "bg-gray-400"}`} />
                                    {enviado ? "ENVIADO" : "ENVIADO?"}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="gap-1"
              data-testid="prevendas-close-btn"
            >
              <X className="h-4 w-4" /> Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
